import styles from './MicrosoftGraphConnection.module.css';

import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import BasicAuthSection from '../authSections/BasicAuthSection';
import OboJwtSection from '../authSections/OboJwtSection';
import SamlBearerSection from '../authSections/SamlBearerSection';
import MicrosoftGraphOAuthSection from './MicrosoftGraphOAuthSection';
import ApiKeyAuthSection from '../authSections/ApiKeyAuthSection';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { authFieldsSchema } from '../authSections/types';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { Link as LinkIcon } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';

// Extend authFieldsSchema to include tenant_id in oauth
const microsoftGraphAuthFieldsSchema = authFieldsSchema.extend({
    oauth: z
        .object({
            token_url: z.string().url().optional().nullable().or(z.literal('')),
            client_id: z.string().optional().nullable(),
            client_secret: z.string().optional().nullable(),
            scope: z.string().optional().nullable(),
            tenant_id: z.string().optional().nullable(),
        })
        .optional()
        .default({ token_url: '', client_id: '', client_secret: '', scope: '', tenant_id: 'common' }),
});

const microsoftGraphSchema = z.object({
    name: z.string().min(1, 'Connection name is required'),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    baseUrl: z.string().url('Invalid URL'),
    timeoutMs: z.number().min(1000, 'Timeout must be at least 1000ms').default(30000),
    authType: z.enum(['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer']),
    testPath: z.string().optional().nullable(),
    ...microsoftGraphAuthFieldsSchema.shape
});

type MicrosoftGraphForm = z.infer<typeof microsoftGraphSchema>;

export default function MicrosoftGraphConnection() {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState<any | null>(null);
    const user = useAuthStore(state => state.user)
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<MicrosoftGraphForm>({
        resolver: zodResolver(microsoftGraphSchema),
        defaultValues: {
            name: '',
            environment: 'prod' as 'test' | 'prod' | 'dev',
            baseUrl: 'https://graph.microsoft.com/v1.0',
            timeoutMs: 30000,
            authType: 'oauth2_client_credentials',
            testPath: '',
            basic: { username: '', password: '' },
            oauth: {
                token_url: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
                client_id: '',
                client_secret: '',
                scope: 'https://graph.microsoft.com/.default',
                tenant_id: 'common',
            },
            obo: { token_url: '', audience: '', resource: '', issuer_discovery: '' },
            saml: { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' },
            apikey: { key_name: '', key_value: '', in: 'header' },
        },
    });
    const validAuthTypes = ['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer'] as const;
    const authType = watch('authType') || 'oauth2_client_credentials';

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user])

    useEffect(() => {
        const configData = connectors.find(c => c.id === configId) || null;
        if (configData) {
            setConfig(configData)
            setValue('name', configData.connectionName || '');
            setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
            setValue('baseUrl', configData.config?.baseUrl || 'https://graph.microsoft.com/v1.0');
            setValue('timeoutMs', configData.config?.timeoutMs || 30000);
            setValue(
                'authType',
                validAuthTypes.includes(configData.authType as any)
                    ? configData.authType as typeof validAuthTypes[number]
                    : 'oauth2_client_credentials'
            );
            setValue('basic', configData.authFields?.basic || { username: '', password: '' });
            setValue('oauth', {
                token_url: configData.authFields?.oauth?.token_url || 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
                client_id: configData.authFields?.oauth?.client_id || '',
                client_secret: configData.authFields?.oauth?.client_secret || '',
                scope: configData.authFields?.oauth?.scope || 'https://graph.microsoft.com/.default',
                tenant_id: configData.authFields?.oauth?.tenant_id || 'common',
            });
            setValue('obo', configData.authFields?.obo || { token_url: '', audience: '', resource: '', issuer_discovery: '' });
            setValue('saml', configData.authFields?.saml || { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' });
            setValue('apikey', configData.authFields?.apikey || { key_name: '', key_value: '', in: 'header' });
            setValue('testPath', configData.config?.testPath || '');
        }
    }, [connectors, configId]);

    const onSubmit = async (data: MicrosoftGraphForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true);
        try {
            // Build authFields object based on selected auth type
            const authFields: any = {};
            if (data.authType === 'api_key') {
                authFields.apikey = data.apikey;
            } else if (data.authType === 'basic') {
                authFields.basic = data.basic;
            } else if (data.authType === 'oauth2_client_credentials') {
                authFields.oauth = data.oauth;
            } else if (data.authType === 'oauth2_obo_jwt') {
                authFields.obo = data.obo;
            } else if (data.authType === 'saml2_bearer') {
                authFields.saml = data.saml;
            }

            const payload = {
                connectionName: data.name,
                environment: data.environment,
                config: {
                    baseUrl: data.baseUrl,
                    timeoutMs: data.timeoutMs,
                    testPath: data.testPath || '',
                },
                userId: user?.id!,
                authType: data.authType,
                authFields,
                defaultHeaders: {
                    'Content-Type': 'application/json',
                },
            };

            let response;
            if (!config) response = await configApi.post(payload);
            else response = await configApi.put(config.id, payload);

            setConfig(response);
            updateConnection(response);
            toast.success('Configuration saved successfully!');
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
        }

        // Get current form values
        const formData = watch();
        
        // Build authFields object based on selected auth type
        const authFields: any = {};
        if (formData.authType === 'api_key') {
            authFields.apikey = formData.apikey;
        } else if (formData.authType === 'basic') {
            authFields.basic = formData.basic;
        } else if (formData.authType === 'oauth2_client_credentials') {
            authFields.oauth = formData.oauth;
        } else if (formData.authType === 'oauth2_obo_jwt') {
            authFields.obo = formData.obo;
        } else if (formData.authType === 'saml2_bearer') {
            authFields.saml = formData.saml;
        }

        const connectionData = {
            connectionName: formData.name,
            environment: formData.environment,
            config: {
                baseUrl: formData.baseUrl,
                timeoutMs: formData.timeoutMs,
                testPath: formData.testPath || '',
            },
            authType: formData.authType,
            authFields,
            defaultHeaders: {
                'Content-Type': 'application/json',
            },
        };

        await testConnectionApi(config.id, setTesting, connectionData, user);
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>Microsoft Graph API Connection</h1>
                    <p>Configure your Microsoft Graph API connection settings</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        onClick={goBack}
                        className={styles.cancelBtn}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !config}
                        className={styles.saveBtn}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className={styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                {/* BASIC SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Core endpoint configuration for Microsoft Graph API.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name *"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                required
                                className={styles.input}
                                externalError={errors.name?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment *</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {setValue('environment', e.target.value as 'test' | 'prod' | 'dev'); 
                                    setError('environment', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="url"
                                label="Base URL *"
                                icon={<LinkIcon size={16} />}
                                value={watch('baseUrl') || ''}
                                onChange={(value) => setValue('baseUrl', value)}
                                required
                                className={styles.input}
                                externalError={errors.baseUrl?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Timeout (ms) *</label>
                            <input
                                type="number"
                                value={watch('timeoutMs') || 30000}
                                onChange={(e) => setValue('timeoutMs', parseInt(e.target.value) || 30000)}
                                className={styles.input}
                                min={1000}
                            />
                            {errors.timeoutMs && (
                                <span className={styles.error}>{errors.timeoutMs.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Authentication Type *</label>
                            <select
                                value={watch('authType')}
                                onChange={(e) => {setValue('authType', e.target.value as any); 
                                    setError('authType', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.authType ? styles.selectError : ''}`}
                            >
                                <option value="none">No Authentication</option>
                                <option value="api_key">API Key</option>
                                <optgroup label="Standard">
                                    <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
                                    <option value="basic">Basic Authentication</option>
                                </optgroup>
                                <optgroup label="Principal Propagation">
                                    <option value="oauth2_obo_jwt">OAuth2 On-Behalf-Of (JWT Bearer)</option>
                                    <option value="saml2_bearer">SAML2 Bearer Assertion</option>
                                </optgroup>
                            </select>
                            {errors.authType && (
                                <span className={styles.error}>{errors.authType.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Test Path (optional)"
                                value={watch('testPath') || ''}
                                onChange={(value) => setValue('testPath', value)}
                                placeholder="Relative path for connection testing"
                                className={styles.input}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* API KEY */}
                {authType === 'api_key' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>API Key</CardTitle>
                            <CardDescription>Send an API key as a header or query parameter.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ApiKeyAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* BASIC AUTH */}
                {authType === 'basic' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>Basic Authentication</CardTitle>
                            <CardDescription>Use only for legacy systems and development.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <BasicAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 CLIENT CREDENTIALS */}
                {authType === 'oauth2_client_credentials' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 Client Credentials</CardTitle>
                            <CardDescription>Configure OAuth2 authentication for Microsoft Graph API.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <MicrosoftGraphOAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 OBO JWT */}
                {authType === 'oauth2_obo_jwt' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 On-Behalf-Of (JWT Bearer)</CardTitle>
                            <CardDescription>Exchange IAS token for Microsoft Graph API access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OboJwtSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* SAML2 BEARER */}
                {authType === 'saml2_bearer' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>SAML2 Bearer Assertion</CardTitle>
                            <CardDescription>Exchange a signed SAML assertion for access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <SamlBearerSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}
            </form>
        </div>
    );
}
